import { useQuery } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import { RelatedOrgsConnectionDocument } from '@/gql/graphql'
import { useContextStore } from '@/lib/session/context-store'
import type { ClientScopeOption } from '@/components/team/ClientScopeSelector'

/**
 * EVERY client the active org manages, drained page by page, with the server's own `total` as the completeness check.
 *
 * Why this exists next to `useMyManagedClients`: that hook reads ONE page of `DEFAULT_RELATED_ORGS_LIMIT` (50), sorted by
 * name, with no truncation signal. An agency with 60 clients would get 50 back and look complete, so a portfolio view built
 * on it would silently show 50 clients' work as "all clients": a confident partial set. This hook fails closed instead:
 * if the drain cannot reach the server's `total` (a page comes back empty early, or the page ceiling is hit) it THROWS, so
 * the caller renders "unavailable", never a shorter list.
 *
 * The page ceiling matches the resolver's own readable-set cap (5000): beyond it the calendar request would be refused as
 * too broad anyway.
 */
export const MANAGED_CLIENTS_PAGE_SIZE = 100
export const MANAGED_CLIENTS_MAX_PAGES = 50

export interface AllManagedClientsResult {
  clients: ClientScopeOption[]
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export async function drainManagedClients(): Promise<ClientScopeOption[]> {
  // Keyed by tenant id: an overlap between pages (a client inserted mid-drain shifts every later row, or a tie in the name sort) would otherwise put
  // the same client in twice and leave another out, with the row COUNT still reaching `total` and no error.
  const byTenant = new Map<string, ClientScopeOption>()
  let read = 0 // rows the SERVER returned so far: the next offset (not the distinct count, which an overlap makes smaller)
  let total = Number.POSITIVE_INFINITY
  let lastPageFull = false
  const fetchPage = (offset: number) =>
    fetchGql(RelatedOrgsConnectionDocument, {
      kinds: ['MANAGES'],
      direction: 'SOURCE',
      persona: 'CLIENT',
      pagination: { limit: MANAGED_CLIENTS_PAGE_SIZE, offset },
    }).then((r) => r.relatedOrgsConnection)

  for (let page = 0; page < MANAGED_CLIENTS_MAX_PAGES && read < total; page++) {
    const conn = await fetchPage(read)
    total = conn.total
    if (conn.items.length === 0) break
    read += conn.items.length
    lastPageFull = conn.items.length >= MANAGED_CLIENTS_PAGE_SIZE
    for (const o of conn.items) byTenant.set(o.tenantId, { tenantId: o.tenantId, displayName: o.name })
  }

  // The server's `total` is what makes the drain checkable, so a `total` that is too SMALL must not go unnoticed: if we stopped exactly on a
  // full page, ask once more. One extra request, only when the roster is an exact multiple of the page size.
  if (read === total && lastPageFull) {
    const probe = await fetchPage(read)
    if (probe.items.length > 0) read += probe.items.length // more rows than the server's own total said: caught by the check below
  }

  if (read !== total || byTenant.size !== read) {
    throw new Error(
      `managed clients: drained ${byTenant.size} distinct of ${total} (server rows read: ${read}); refusing to present a partial list as the whole portfolio`,
    )
  }
  return [...byTenant.values()]
}

export function useAllManagedClients(enabled = true): AllManagedClientsResult {
  const tenantId = useContextStore((s) => s.active?.tenantId)
  const q = useQuery({
    queryKey: ['relationships', 'all-managed-clients', tenantId ?? 'self'],
    // `enabled` lets a surface that is only SOMETIMES a portfolio (the invoices cockpit: agency yes,
    // client no) skip the read for the personas that have no clients to list.
    enabled: enabled && Boolean(tenantId),
    queryFn: drainManagedClients,
    staleTime: 5 * 60 * 1000,
  })
  // `isPending`, NOT `isLoading`: with no active tenant yet the query is DISABLED, and a disabled query is not `isLoading`
  // but has no answer either. Reporting that as "loaded, zero clients" would render "No clients in scope" before the
  // caller's context exists (a confident zero), so it stays pending until there is a real answer or a real error.
  return { clients: q.data ?? [], isLoading: q.isPending && !q.isError, isError: q.isError, refetch: () => void q.refetch() }
}
